import { Alert, IconButton, Skeleton, Tooltip } from '@mui/material';
import React from 'react';
import { GlobalHotKeys } from 'react-hotkeys';
import IconeEspelhoProcesso from '../img/icones/IconeEspelhoProcesso';
import { runInAction } from 'mobx';
import { Processo } from '../modelo';
import { Minuta } from '../modelo/Minuta';
import { Peca } from '../modelo/dominio/Peca';
import { IMovimentoInternoEsij } from '../modelo/MovimentoInternoEsij';
import { OrigemProcesso } from '../modelo/OrigemProcesso';
import { DadosProcessoDto } from '../modelo/dto/DadosProcessoDto';
import { SituacaoMinuta } from '../modelo/SituacaoMinuta';
import InformacoesProcessoService, { FALHA_CONSULTA_PRINCIPAL_PROCESSO } from '../services/InformacoesProcessoService';
import MinutaService from '../services/MinutaService';
import MinutaApi from '../api/MinutaApi';
import ProcessoApi from '../api/ProcessoApi';
import ProcessoExpandidoStore from '../store/ProcessoExpandidoStore';
import RouterStore, { FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY, PARAM_ESPELHO_DEPENDENTE } from '../store/RouterStore';
import UsuarioStore from '../store/UsuarioStore';
import ViewMinutas from './abas-processo/ViewMinutas';
import InformacoesProcessoView from './editor/InformacoesProcessoView';
import PartesProcesso from './editor/PartesProcesso';
import ResponsaveisMinutas from './editor/ResponsaveisMinutas';
import ViewMovimentoProcesso from './escaninho/ViewMovimentoProcesso';
import ViewMovimentoProcessoEsij from './escaninho/ViewMovimentoProcessoEsij';
import { ViewPecasProcesso } from './escaninho/ViewPecasProcesso';
import { ProcessoExterno } from '../modelo/ProcessoExterno';
import { mapPecasProcesso } from '../services/ProcessoExpandidoService';

interface IPropsEspelhoProcesso {
    idProcesso?: number;
    botaoAbrirEspelho?: boolean;
}

interface IEspelhoState {
    idProcesso?: number;
    carregando: boolean;
    carregado: boolean;
    erro: string;
    tentativas: number;
    ultimoCarregamentoEm: string;
    dados?: DadosProcessoDto;
    pecas: Peca[];
    carregandoPecas: boolean;
    minutas: Minuta[];
    movimentoInternoEsij: IMovimentoInternoEsij[];
    gabinetesProcessoImportado?: string[];
}

/** DadosProcessoDto conforme retornado pela API, que às vezes inclui um campo `minutas` não declarado no DTO. */
type DadosProcessoComMinutas = DadosProcessoDto & { minutas?: Minuta[] };

class EspelhoProcesso extends React.Component<IPropsEspelhoProcesso, IEspelhoState> {
    private desmontado = false;
    intervaloVerificacaoDependencia: number = null;
    sinalFechamentoDependenciaInicial = '';
    tituloDocumentoAnterior = '';
    tituloDocumentoAlterado = false;

    constructor(props: IPropsEspelhoProcesso) {
        super(props);

        this.state = {
            idProcesso: this.recuperarIdProcesso(),
            carregando: false,
            carregado: false,
            erro: '',
            tentativas: 0,
            ultimoCarregamentoEm: '',
            dados: undefined,
            pecas: [],
            carregandoPecas: false,
            minutas: [],
            movimentoInternoEsij: [],
            gabinetesProcessoImportado: undefined,
        };
    }

    componentDidMount(): void {
        if (!this.props.botaoAbrirEspelho) {
            this.definirTituloDocumento();
            window.addEventListener('storage', this.fecharEspelhoDependente);
            this.iniciarVerificacaoDependencia();
            RouterStore.setErroCarregamentoEspelho(false);
            RouterStore.setDadosProcessoEspelho(null);
            RouterStore.setProcessoImportacaoEspelho(null);
            this.carregar();
        }
    }

    componentDidUpdate(prevProps: IPropsEspelhoProcesso): void {
        const mudouId = prevProps.idProcesso !== this.props.idProcesso;
        const mudouModo = prevProps.botaoAbrirEspelho !== this.props.botaoAbrirEspelho;

        if ((mudouId || mudouModo) && !this.props.botaoAbrirEspelho) {
            this.carregar(true);
        }
    }

    componentWillUnmount(): void {
        this.desmontado = true;
        if (!this.props.botaoAbrirEspelho) {
            window.removeEventListener('storage', this.fecharEspelhoDependente);
            this.pararVerificacaoDependencia();
            RouterStore.setErroCarregamentoEspelho(false);
            RouterStore.setDadosProcessoEspelho(null);
            RouterStore.setProcessoImportacaoEspelho(null);
            this.restaurarTituloDocumento();
        }
    }

    definirTituloDocumento = () => {
        if (this.tituloDocumentoAlterado) {
            return;
        }

        this.tituloDocumentoAnterior = document.title;
        document.title = 'Espelho do Processo';
        this.tituloDocumentoAlterado = true;
    }

    restaurarTituloDocumento = () => {
        if (!this.tituloDocumentoAlterado) {
            return;
        }

        document.title = this.tituloDocumentoAnterior || 'Gabinete Eletrônico';
        this.tituloDocumentoAlterado = false;
    }

    isEspelhoDependente = () => {
        return new URLSearchParams(window.location.search).get(PARAM_ESPELHO_DEPENDENTE) === 'true';
    }

    iniciarVerificacaoDependencia = () => {
        if (!this.isEspelhoDependente() || this.intervaloVerificacaoDependencia) {
            return;
        }

        this.sinalFechamentoDependenciaInicial = localStorage.getItem(FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY) || '';
        this.intervaloVerificacaoDependencia = window.setInterval(() => {
            if (window.opener && window.opener.closed) {
                window.close();
                return;
            }

            const sinalFechamentoAtual = localStorage.getItem(FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY) || '';
            if (sinalFechamentoAtual && sinalFechamentoAtual !== this.sinalFechamentoDependenciaInicial) {
                window.close();
            }
        }, 1000);
    }

    pararVerificacaoDependencia = () => {
        if (this.intervaloVerificacaoDependencia) {
            window.clearInterval(this.intervaloVerificacaoDependencia);
            this.intervaloVerificacaoDependencia = null;
        }
    }

    fecharEspelhoDependente = (event: StorageEvent) => {
        if (event.key !== FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY) {
            return;
        }

        if (this.isEspelhoDependente()) {
            window.close();
        }
    }

    private withTimeoutReject = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error(`Timeout ao carregar ${label}.`)), ms);
            promise
                .then((value) => {
                    clearTimeout(timer);
                    resolve(value);
                })
                .catch((error) => {
                    clearTimeout(timer);
                    reject(error);
                });
        });
    }

    private recuperarIdProcesso = (): number => {
        if (this.props.botaoAbrirEspelho) {
            return Number(this.props.idProcesso);
        }

        const idViaParams = Number(RouterStore.params?.idProcesso);
        if (Number.isFinite(idViaParams) && idViaParams > 0) {
            return idViaParams;
        }

        const match = window.location.pathname.match(/\/espelhoProcesso\/(\d+)/);
        const idViaPath = Number(match?.[1]);
        if (Number.isFinite(idViaPath) && idViaPath > 0) {
            return idViaPath;
        }

        return NaN;
    }

    private carregar = async (forcar = false): Promise<void> => {
        if (this.props.botaoAbrirEspelho) {
            return;
        }

        if (!forcar && (this.state.carregando || this.state.carregado)) {
            return;
        }

        const idProcesso = this.recuperarIdProcesso();
        if (!Number.isFinite(idProcesso) || idProcesso <= 0) {
            this.setState({
                idProcesso: undefined,
                erro: 'Não foi possível identificar o processo para abrir o espelho.',
                carregando: false,
                carregado: false,
            });
            return;
        }

        this.setState((prev) => ({
            idProcesso,
            carregando: true,
            carregado: false,
            erro: '',
            tentativas: prev.tentativas + 1,
            ultimoCarregamentoEm: new Date().toISOString(),
            gabinetesProcessoImportado: undefined,
        }));

        try {
            const dados = await this.withTimeoutReject(
                InformacoesProcessoService.consultarProcesso(idProcesso, true, { carregarPecas: false }),
                30000,
                'dados do espelho'
            );

            if (this.desmontado) {
                return;
            }

            this.setState({
                dados,
                carregando: false,
                carregado: true,
                erro: '',
            });
            RouterStore.setErroCarregamentoEspelho(false);
            RouterStore.setDadosProcessoEspelho(dados);
            RouterStore.setProcessoImportacaoEspelho(ProcessoExterno.novoProcessoExternoParaImportar(dados));

            this.carregarPecasEMinutas(idProcesso);
            this.carregarGabinetesProcessoImportado(idProcesso);
            this.carregarRelacionamentos(idProcesso, dados);

            if (dados?.origem === 'L') {
                const andamentoInterno = await ProcessoApi
                    .recuperaAndamentoInternoNoGabinete(idProcesso, UsuarioStore.gabinete)
                    .catch(() => []);
                if (!this.desmontado) {
                    this.setState({
                        movimentoInternoEsij: Array.isArray(andamentoInterno) ? andamentoInterno : [],
                    });
                }
            }
        } catch (error: any) {
            if (this.desmontado) {
                return;
            }

            if (error?.[FALHA_CONSULTA_PRINCIPAL_PROCESSO]) {
                RouterStore.setErroCarregamentoEspelho(true);
            }

            this.setState({
                carregando: false,
                carregado: false,
                erro: `Falha ao carregar dados do processo: ${error?.message || 'erro desconhecido'}`,
            });
        }
    }

    private carregarGabinetesProcessoImportado = async (idProcesso: number): Promise<void> => {
        try {
            const gabinetesProcessoImportado = await ProcessoApi.buscarGabinetesOndeProcessoEstaImportado(idProcesso);
            if (!this.desmontado && this.state.idProcesso === idProcesso) {
                this.setState({ gabinetesProcessoImportado });
            }
        } catch (error) {
            console.warn('Falha ao carregar as unidades onde o processo está importado.', error);
        }
    }

    private carregarRelacionamentos = async (idProcesso: number, dados: DadosProcessoDto): Promise<void> => {
        try {
            let processosAssociados = dados.processosAssociados || [];
            let correJuntos = dados.correJuntos || [];
            if (dados.origem === 'L') {
                const correJuntosPorProcesso = await ProcessoApi.recuperaProcessosComCorreJunto([idProcesso]);
                correJuntos = correJuntosPorProcesso[idProcesso] || [];
            } else {
                const associadosPorProcesso = await ProcessoApi.recuperaProcessosAssociados([idProcesso]);
                processosAssociados = associadosPorProcesso[idProcesso] || [];
            }
            if (!this.desmontado && this.state.idProcesso === idProcesso) {
                runInAction(() => {
                    dados.processosAssociados = processosAssociados;
                    dados.correJuntos = correJuntos;
                });
                this.setState({ dados });
                RouterStore.setDadosProcessoEspelho(dados);
            }
        } catch (error) {
            console.warn('Falha ao carregar os processos relacionados no espelho.', error);
        }
    }

    private abrirEspelho = (): void => {
        RouterStore.abrirJanela('/espelhoProcesso/' + this.props.idProcesso);
    }

    private ehArquivada = (m: Minuta): boolean => {
        const codigo = Number(m?.situacaoMinuta?.codigo);
        const descricao = String(m?.situacaoMinuta?.descricao || '').toUpperCase();
        return codigo === SituacaoMinuta.ARQUIVADA || descricao.includes('ARQUIVADA');
    };

    // Retorna todas as minutas não-arquivadas (para a barra superior)
    private minutasParaTopBar = (minutas: Minuta[] = []): Minuta[] => {
        return (minutas || [])
            .filter((m) => !!m)
            .filter((m) => !this.ehArquivada(m));
    }

    // Deduplica por número de minuta
    private deduplicarMinutas = (minutas: Minuta[]): Minuta[] => {
        const vistas = new Set<number>();
        return minutas.filter((m) => {
            const num = Number(m?.numero);
            if (!num || vistas.has(num)) return false;
            vistas.add(num);
            return true;
        });
    }

    private carregarPecasEMinutas = async (idProcesso: number): Promise<void> => {
        this.setState({ carregandoPecas: true });

        try {
            const [pecasApi, peticoesPendentes, minutasAtivasRaw, minutasAssinadas, minutasArquivadas] = await Promise.all([
                InformacoesProcessoService.consultarDocumentosProcesso(idProcesso).catch(() => []),
                InformacoesProcessoService.consultarPeticoesPendentes(idProcesso).catch(() => []),
                MinutaApi.recuperaMinutas(idProcesso).catch(() => []),
                MinutaService.consultarMinutasAssinadas(idProcesso).catch(() => []),
                MinutaService.consultarMinutasArquivadas(idProcesso).catch(() => []),
            ]);

            if (this.desmontado) {
                return;
            }

            const processoPai = Processo.novo({ identificadorProcesso: idProcesso });

            // Minutas ativas (em elaboração, em revisão, liberadas, devolvidas...)
            // MinutaApi.recuperaMinutas não é tipado (retorna Promise<unknown>), daí o cast pontual.
            const minutasAtivas = this.deduplicarMinutas(
                (minutasAtivasRaw as any[]).map((m) => Minuta.criar(m, processoPai))
            );

            // Popula o ProcessoExpandidoStore para que ViewMinutas exiba as categorias corretamente
            runInAction(() => {
                ProcessoExpandidoStore.minutas = minutasAtivas;
            });

            // Para a barra superior: todas as minutas não-arquivadas
            const todasCombinadas = this.deduplicarMinutas([
                ...minutasAtivas,
                ...minutasAssinadas,
                ...minutasArquivadas,
            ]);

            this.setState((prev) => ({
                minutas: this.minutasParaTopBar(todasCombinadas),
                pecas: mapPecasProcesso(
                    pecasApi?.length ? pecasApi : (prev.dados?.pecas || []),
                    peticoesPendentes,
                    prev.dados?.origem
                ),
            }));
        } catch (error) {
            console.warn('Falha ao carregar pecas/minutas do espelho.', error);
        } finally {
            if (!this.desmontado) {
                this.setState({ carregandoPecas: false });
            }
        }
    }

    private imprimir = (): void => {
        const id = this.state.idProcesso || this.recuperarIdProcesso();
        RouterStore.abrirJanela('/impressaoEspelhoProcesso/' + id);
    }

    private numberToReal = (num?: number): string => {
        return num ? num.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    }

    private processoAtual = (): Processo | undefined => {
        const { dados, idProcesso, minutas } = this.state;
        if (!dados) {
            return undefined;
        }

        const processo = Processo.novo({ identificadorProcesso: idProcesso || dados.identificadorProcesso || dados.id });
        DadosProcessoDto.toProcesso(dados, processo);

        if (!processo.classeJudicial) {
            processo.classeJudicial = { sigla: '', descricao: '' };
        }
        if (!processo.orgaoJulgador) {
            processo.orgaoJulgador = { sigla: '', descricao: '' };
        }
        if (!processo.orgaoJulgadorColegiado) {
            processo.orgaoJulgadorColegiado = { sigla: '', descricao: '' };
        }
        if (!processo.origemProcesso) {
            const origem = dados?.origem || '';
            processo.origemProcesso = {
                id: origem as OrigemProcesso,
                descricao: origem === 'P' ? 'PJe' : origem === 'L' ? 'Legado' : (origem || '')
            };
        }

        const minutasDoPayload = (dados as DadosProcessoComMinutas)?.minutas || [];
        const minutasParaProcesso = (minutas && minutas.length > 0) ? minutas : minutasDoPayload;
        processo.minutas = this.minutasParaTopBar(minutasParaProcesso);

        return processo;
    }

    private usuarioLogadoNoGabineteDoProcesso = (processoAtual?: Processo): boolean => {
        const siglaOrgao = processoAtual?.orgaoJulgador?.sigla || processoAtual?.orgaoJulgadorColegiado?.sigla;
        return !!siglaOrgao && UsuarioStore.gabinete === siglaOrgao;
    }

    private renderSkeleton = () => {
        const linha = (width: string, height = 22, marginLeft = 0) => (
            <Skeleton variant='text' width={width} height={height} style={{ marginLeft }} />
        );

        return (
            <div aria-label='Carregando espelho do processo'>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 30%) minmax(0, 34%) minmax(0, 36%)', gap: '10px', width: '100%' }}>
                    <div style={{ minHeight: '76vh', padding: '10px 12px' }}>
                        {linha('53%', 26)}
                        <div style={{ marginTop: '16px' }}>{linha('42%')}</div>
                        <div style={{ marginTop: '14px' }}>{linha('20%')}</div>
                        {linha('34%', 22, 70)}
                        {linha('52%', 22, 100)}
                        {linha('25%', 22, 130)}
                        {linha('58%', 22, 160)}
                        <div style={{ marginTop: '8px' }}>{linha('31%', 22, 70)}</div>
                        {linha('46%', 22, 100)}
                        {linha('29%', 22, 130)}
                        {linha('54%', 22, 160)}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '14px' }}>
                            <Skeleton variant='rounded' width={138} height={30} />
                            <Skeleton variant='rounded' width={172} height={30} />
                            <Skeleton variant='rounded' width={108} height={30} />
                        </div>
                        <div style={{ marginTop: '14px' }}>
                            {linha('46%')}
                            {linha('82%')}
                            {linha('69%')}
                            {linha('52%')}
                            {linha('61%')}
                            {linha('43%')}
                        </div>
                    </div>
                    <div style={{ minHeight: '76vh', padding: '16px 0 0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '36px' }}>
                            <Skeleton variant='rounded' width='100%' height={36} />
                            <Skeleton variant='rounded' width='100%' height={36} />
                        </div>
                        <div style={{ padding: '10px 18px' }}>
                            {linha('24%', 25)}
                            {[
                                '91%', '84%', '96%', '73%', '89%', '78%',
                                '94%', '86%', '90%', '80%', '95%', '76%',
                                '88%', '82%', '93%', '74%', '87%', '79%'
                            ].map((width, index) => (
                                <div
                                    key={`skeleton-movimento-${index}`}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}
                                >
                                    <Skeleton variant='circular' width={6} height={6} />
                                    <Skeleton variant='text' width={width} height={23} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ minHeight: '76vh', padding: '10px 12px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {linha('25%', 25)}
                            <Skeleton variant='rounded' width={54} height={24} />
                        </div>
                        {linha('52%', 21)}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', margin: '8px 0 12px' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Skeleton variant='rounded' width={32} height={18} />
                                    {linha('130px', 22)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
                                    <Skeleton variant='rounded' width={32} height={18} />
                                    {linha('185px', 22)}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Skeleton variant='rounded' width={143} height={36} />
                                <Skeleton variant='rounded' width={143} height={36} />
                            </div>
                        </div>
                        <div style={{ borderTop: '1px solid #d9e2ec', paddingTop: '10px' }}>
                            {Array.from({ length: 7 }).map((_, index) => (
                                <div
                                    key={`skeleton-peca-${index}`}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '36px minmax(0, 1fr) 30px 76px',
                                        alignItems: 'center',
                                        gap: '10px',
                                        height: '64px',
                                        padding: '0 12px',
                                        marginBottom: '8px',
                                        border: '1px solid #d9e2ec',
                                        borderRadius: '6px'
                                    }}
                                >
                                    <Skeleton variant='rounded' width={34} height={34} />
                                    <div>
                                        {linha(index % 2 === 0 ? '72%' : '88%', 22)}
                                        {linha(index % 2 === 0 ? '45%' : '62%', 18)}
                                    </div>
                                    <Skeleton variant='circular' width={22} height={22} />
                                    <Skeleton variant='rounded' width={76} height={34} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={{ paddingTop: '4px' }}>
                    <div style={{ padding: '0 20px' }}>
                        <Skeleton variant='text' width={85} height={30} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', padding: '16px 18px 10px' }}>
                        {['125px', '112px', '210px', '150px', '115px', '105px', '125px'].map((width, index) => (
                            <div key={`skeleton-filtro-minuta-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Skeleton variant='rounded' width={18} height={18} />
                                <Skeleton variant='text' width={width} height={22} />
                            </div>
                        ))}
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1.1fr 1.3fr .75fr 1.7fr 2.2fr 1.5fr',
                        gap: '24px',
                        alignItems: 'center',
                        minHeight: '54px',
                        padding: '0 18px',
                        borderTop: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0'
                    }}>
                        {['72%', '64%', '58%', '76%', '82%', '70%'].map((width, index) => (
                            <Skeleton key={`skeleton-coluna-minuta-${index}`} variant='text' width={width} height={22} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    private diagnostico = () => {
        const { idProcesso, carregando, carregado, tentativas, ultimoCarregamentoEm, erro, dados, pecas, minutas } = this.state;
        const processoAtual = this.processoAtual();

        return {
            idProcesso,
            botaoAbrirEspelho: !!this.props.botaoAbrirEspelho,
            tokenStore: !!UsuarioStore.token,
            tokenUsuario: !!UsuarioStore.usuario?.token,
            carregandoDados: carregando,
            carregouDados: carregado,
            tentativasCarregamento: tentativas,
            ultimoCarregamentoEm,
            erroCarregamento: erro,
            dadosProcessoCarregado: !!dados,
            procCarregado: !!processoAtual,
            partesAtivo: dados?.partes?.ATIVO?.length || 0,
            partesPassivo: dados?.partes?.PASSIVO?.length || 0,
            movimentos: dados?.movimentos?.length || 0,
            pecas: pecas?.length || dados?.pecas?.length || 0,
            assuntos: dados?.assuntoList?.length || 0,
            alertaAtivos: dados?.alertasAtivos?.length || 0,
            minutas: minutas?.length || 0,
        };
    }

    render() {
        if (this.props.botaoAbrirEspelho) {
            return (
                <Tooltip title='Espelho do Processo'>
                    <IconButton size='small' onClick={this.abrirEspelho} aria-label='Espelho do Processo. Irá abrir em uma nova página.'>
                        <IconeEspelhoProcesso />
                    </IconButton>
                </Tooltip>
            );
        }

        const { dados, erro, pecas, minutas, movimentoInternoEsij } = this.state;

        if (erro) {
            return (
                <Alert
                    severity='error'
                    sx={{
                        margin: '24px',
                        padding: '10px 16px',
                        alignItems: 'center',
                        '& .MuiAlert-icon': {
                            padding: 0
                        },
                        '& .MuiAlert-message': {
                            padding: '2px 0',
                            overflow: 'visible'
                        }
                    }}
                >
                    {erro}
                </Alert>
            );
        }

        if (!dados) {
            return this.renderSkeleton();
        }

        const processoAtual = this.processoAtual();
        const usuarioLogadoNoGabineteDoProcesso = this.usuarioLogadoNoGabineteDoProcesso(processoAtual);
        const movimentos = dados?.movimentos || [];
        const minutasFallback = (dados as DadosProcessoComMinutas)?.minutas || [];
        const minutasParaExibir = this.minutasParaTopBar(minutas?.length ? minutas : minutasFallback);
        const pecasParaExibir = pecas?.length ? pecas : (dados?.pecas || []);
        const dadosParaRender: DadosProcessoComMinutas | undefined = dados
            ? { ...dados, pecas: pecasParaExibir, minutas: minutasParaExibir }
            : undefined;

        return (
            <GlobalHotKeys handlers={{ print: this.imprimir }} keyMap={{ print: 'ctrl+p' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%', gap: '8px' }}>
                        <div className='divFlexRow' style={{ maxHeight: '76vh', minHeight: '76vh', flex: '58 1 0', minWidth: 0, overflow: 'auto' }}>
                            {dadosParaRender && processoAtual ?
                                <span style={{ marginLeft: '10px' }}>
                                    <div>
                                        <InformacoesProcessoView
                                            processo={processoAtual}
                                            gabinetesProcessoImportado={this.state.gabinetesProcessoImportado}
                                            alertas={dadosParaRender?.alertasAtivos || dadosParaRender?.alertas || []}
                                            tarefaFluxoPje={dadosParaRender?.tarefaFluxoPje}
                                            indicadores={dadosParaRender?.indicadores || []}
                                            espelho={false}
                                        >
                                            <PartesProcesso
                                                partes={dadosParaRender?.partes}
                                                mensagemErro={dadosParaRender?.origem === 'L' ? dadosParaRender.mensagemErroPartes : undefined}
                                            />
                                        </InformacoesProcessoView>
                                    </div>
                                    {usuarioLogadoNoGabineteDoProcesso &&
                                        <ResponsaveisMinutas idProcesso={this.state.idProcesso} processo={dadosParaRender || null} />
                                    }
                                </span>
                                :
                                null
                            }
                        </div>

                        <div className='divFlexRow' style={{ maxHeight: '76vh', flex: '66 1 0', minWidth: 0, overflow: 'auto' }}>
                            <div style={{
                                display: 'flex', flexDirection: 'column',
                                marginRight: '10px', marginTop: '15px', width: '100%'
                            }}>
                                {dados?.origem === 'L' ?
                                    <ViewMovimentoProcessoEsij movimentos={movimentos} dadosProcesso={dadosParaRender} movimentoInterno={movimentoInternoEsij} />
                                    :
                                    <ViewMovimentoProcesso movimentos={movimentos} dadosProcesso={dadosParaRender} />}
                            </div>
                        </div>
                        {dadosParaRender &&
                            <div style={{ maxHeight: '76vh', flex: '70 1 0', minWidth: 0, overflow: 'auto' }}>
                                <ViewPecasProcesso
                                    pecas={pecasParaExibir}
                                    dadosProcesso={dadosParaRender}
                                    carregandoPecas={this.state.carregandoPecas}
                                    exibirSituacaoPeticao={false}
                                />
                            </div>
                        }
                    </div>

                    <div>
                        {dadosParaRender &&
                            <ViewMinutas minutas={true} processo={dadosParaRender} espelho={true} />
                        }
                    </div>
                </div>
            </GlobalHotKeys>
        );
    }
}

export default EspelhoProcesso;
